import { z } from 'zod';
import { TodoPlanItem } from './myday.js';
import { PersonRef } from './people.js';
/**
 * Chapi, the assistant (docs/adr/0021-chapi.md, brief "Chapi AI"). It only sees what the asking person
 * can see, runs in Australia by default, and never acts on its own: tasks and replies it proposes are
 * shown for the person to confirm, then saved through the normal routes.
 */
/** What the person asked for. Shortcuts carry the item they started from; `ask` is free text. */
export const ChapiIntent = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('ask'), text: z.string().trim().min(1).max(4000) }),
    z.object({ kind: z.literal('summarise_channel'), channelId: z.uuid() }),
    z.object({ kind: z.literal('summarise_conversation'), messageId: z.uuid() }),
    z.object({ kind: z.literal('draft_reply'), messageId: z.uuid() }),
    z.object({ kind: z.literal('make_task'), messageId: z.uuid() }),
]);
export const AskChapiInput = z.object({
    /** Continue a conversation; omitted starts a new one. */
    conversationId: z.uuid().optional(),
    intent: ChapiIntent,
});
export const ChapiPriority = z.enum(['p1', 'p2', 'p3', 'p4']);
/** A task Chapi suggests. Nothing is saved until the person confirms (createWorkItem). */
export const ChapiTaskProposal = z.object({
    kind: z.literal('task'),
    title: z.string().max(200),
    description: z.string().max(4000),
    projectId: z.uuid().nullable(),
    projectName: z.string().nullable(),
    assignee: PersonRef.nullable(),
    /** YYYY-MM-DD in the workspace's time zone. */
    dueOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
    priority: ChapiPriority.nullable(),
    estimateMinutes: z.number().int().positive().max(100_000).nullable(),
    sourceMessageId: z.uuid().nullable(),
    /** The stage it starts in, when the request names one ("stage In progress"); older answers have none (docs/adr/0094). */
    statusId: z.uuid().nullable().optional(),
    statusLabel: z.string().max(40).nullable().optional(),
    /** The list section it goes in, when the request names one ("in the Forms section") and the project has it. */
    sectionId: z.uuid().nullable().optional(),
    sectionName: z.string().max(80).nullable().optional(),
});
/** A reply Chapi drafted. The person edits and sends it themselves (postMessage). */
export const ChapiReplyProposal = z.object({
    kind: z.literal('reply'),
    channelId: z.uuid(),
    channelName: z.string(),
    replyToMessageId: z.uuid().nullable(),
    text: z.string().max(10000),
});
export const ChapiAction = z.discriminatedUnion('kind', [ChapiTaskProposal, ChapiReplyProposal]);
/** What an answer was based on, so the person can open it. */
export const ChapiSource = z.object({
    type: z.enum(['message', 'channel', 'work_item']),
    id: z.uuid(),
    label: z.string(),
    /** For messages: the channel to open it in. */
    channelId: z.uuid().nullable(),
});
export const ChapiMessage = z.object({
    id: z.uuid(),
    role: z.enum(['user', 'assistant']),
    text: z.string(),
    /** On the person's messages: what they asked for, so a failed answer can be asked again. */
    intent: ChapiIntent.nullable(),
    actions: z.array(ChapiAction),
    sources: z.array(ChapiSource),
    /** Assistant answers stream in (realtime `chapi.delta`); `done` or `failed` when finished. */
    status: z.enum(['streaming', 'done', 'failed']),
    error: z.string().nullable(),
    createdAt: z.string(),
});
export const ChapiConversationSummary = z.object({ id: z.uuid(), title: z.string(), updatedAt: z.string() });
export const ChapiConversation = ChapiConversationSummary.extend({ messages: z.array(ChapiMessage) });
export const ChapiConversationList = z.object({ items: z.array(ChapiConversationSummary) });
export const AskChapiResponse = z.object({
    conversation: ChapiConversationSummary,
    question: ChapiMessage,
    /** Starts as `streaming`; the text arrives over realtime, and the final message with `chapi.done`. */
    answer: ChapiMessage,
});
/** Whether Chapi can answer here, and starting points for the person. */
export const ChapiStatus = z.object({
    /** False when the server has no model set up, or the workspace's monthly budget is used up. */
    available: z.boolean(),
    reason: z.enum(['not_configured', 'budget_used']).nullable(),
    modelPath: z.enum(['australia_only', 'anthropic_zdr']),
    suggestions: z.array(z.object({ label: z.string(), intent: ChapiIntent })),
});
/** This month's use (owners and admins): tokens, an estimated cost, and who used it. */
export const ChapiUsage = z.object({
    month: z.string().regex(/^\d{4}-\d{2}$/),
    requests: z.number().int(),
    inputTokens: z.number().int(),
    outputTokens: z.number().int(),
    /** Estimated from list prices; the AWS or Anthropic bill is the record. */
    costUsd: z.number(),
    budgetTokens: z.number().int(),
    byPerson: z.array(z.object({ person: PersonRef, requests: z.number().int(), tokens: z.number().int() })),
});
/**
 * Chapi's morning summary for My work (brief: "morning summary on My work"). Written by the worker
 * once per working day per person, shortly before their start-of-day time, from what they can see.
 */
export const ChapiMorning = z.object({
    /** YYYY-MM-DD in the person's time zone. */
    day: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    text: z.string(),
    /** Up to two follow-ups, each asked of Chapi when tapped. */
    actions: z.array(z.object({ label: z.string(), intent: ChapiIntent })).max(2),
    sources: z.array(ChapiSource),
    /** Plan my day (docs/adr/0029-my-day.md): a proposed list for My day, accepted with a tap. */
    plan: z.array(TodoPlanItem),
    writtenAt: z.string(),
});
/** Null until today's summary is written, when it is switched off, or when Chapi isn't available. */
export const ChapiMorningResponse = z.object({ summary: ChapiMorning.nullable() });
//# sourceMappingURL=chapi.js.map